import { useCallback, useState } from "react";
import { AxiosError } from "axios";
import { ChangeMarketingPreferences } from "../../Api/Account";

type UseMarketingUpdateArgs = {
    getValidAccessToken: () => Promise<string | null>;
};

type MarketingUpdateResult =
    | { ok: true }
    | { ok: false; status?: number; code?: string; error?: AxiosError | Error };

function extractErrorCode(err: AxiosError): { code?: string; status?: number } {
    const status = err.response?.status;
    const data = err.response?.data as any;

    if (!data) return { status };

    if (typeof data === "string") {
        return { code: data, status };
    }

    if (typeof data.error === "string") {
        return { code: data.error, status };
    }

    return { status };
}

function mapErrorCodeToMessage(code?: string, status?: number): string {
    if (!code) {
        if (status === 401) return "Your session has expired. Please log in again.";
        return "Something went wrong while updating your preferences.";
    }

    switch (code) {
        case "invalid_token":
            return "Your session has expired. Please log in again.";
        case "user_not_found":
            return "Account not found.";
        case "empty_update":
            return "No changes were supplied.";
        default:
            return "Something went wrong while updating your preferences.";
    }
}

export function useUpdateMarketingPreferences({ getValidAccessToken }: UseMarketingUpdateArgs) {
    const [isUpdating, setIsUpdating] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const clearError = useCallback(() => setErrorMessage(null), []);
    const clearSuccess = useCallback(() => setSuccess(false), []);

    const updateMarketingPrefs = useCallback(
        async (prefs: { allowCommercialEmails?: boolean; allowDailyNewsletter?: boolean }): Promise<MarketingUpdateResult> => {
            const token = await getValidAccessToken();
            if (!token) {
                const err = new Error("Not authenticated");
                setErrorMessage("You must be logged in to update your preferences.");
                setSuccess(false);
                return { ok: false, error: err };
            }

            const controller = new AbortController();
            setIsUpdating(true);
            setErrorMessage(null);
            setSuccess(false);

            try {
                const res = await ChangeMarketingPreferences(prefs, token, controller);

                if (res === true) {
                    setSuccess(true);
                    return { ok: true };
                }

                const axiosErr = res as AxiosError;
                const { code, status } = extractErrorCode(axiosErr);
                const msg = mapErrorCodeToMessage(code, status);

                setErrorMessage(msg);
                setSuccess(false);

                return { ok: false, code, status, error: axiosErr };
            } catch (err) {
                setErrorMessage("Something went wrong while updating your preferences.");
                setSuccess(false);
                return { ok: false, error: err as Error };
            } finally {
                controller.abort();
                setIsUpdating(false);
            }
        },
        [getValidAccessToken]
    );

    return {
        updateMarketingPrefs,
        isUpdating,
        errorMessage,
        success,
        clearError,
        clearSuccess,
    };
}
